import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { CamposLocalidade } from '../components/CamposLocalidade';
import { PAISES } from '../dados/paises';
import { UFS } from '../dados/ufs-br';
import {
  Campo,
  CampoBusca,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
  Paginacao,
  Vazio,
} from '../components/ui';

interface Evento {
  id: string;
  name: string;
  shortDescription: string | null;
  type: string | null;
  format: string;
  city: string | null;
  state: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  _count?: { participants: number };
}

/** Mesma paleta que o backend usa como semântica de estado. */
export const CORES_STATUS: Record<string, string> = {
  RASCUNHO: 'muted',
  EM_CONFIGURACAO: 'info',
  PRONTO: 'accent',
  ATIVO: 'success',
  PAUSADO: 'warning',
  ENCERRADO: 'muted',
  ARQUIVADO: 'muted',
};

export const ROTULOS_STATUS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  EM_CONFIGURACAO: 'Em configuração',
  PRONTO: 'Pronto',
  ATIVO: 'Ativo',
  PAUSADO: 'Pausado',
  ENCERRADO: 'Encerrado',
  ARQUIVADO: 'Arquivado',
};

export function Eventos() {
  const { pode } = useAuth();
  const cliente = useQueryClient();
  const navegar = useNavigate();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [criando, setCriando] = useState(false);

  const params = new URLSearchParams({ page: String(pagina), perPage: '20' });
  if (busca) params.set('search', busca);
  if (status) params.set('status', status);

  const consulta = useQuery({
    queryKey: ['eventos', pagina, busca, status],
    queryFn: () => api<Paginado<Evento>>(`/events?${params}`),
  });

  const criar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api<Evento>('/events', { method: 'POST', body: limpar(dados) }),
    onSuccess: (evento) => {
      cliente.invalidateQueries({ queryKey: ['eventos'] });
      setCriando(false);
      // Direto para o evento novo: ele nasce como RASCUNHO com quase nada
      // preenchido, e voltar para a lista deixava a pessoa procurando onde
      // continuar o que acabou de começar.
      navegar(`/eventos/${evento.id}`);
    },
  });

  return (
    <>
      <PageHeader
        titulo="Eventos"
        subtitulo="Cada evento tem seus próprios participantes, jornada e recomendações."
        acoes={
          pode('events.create') && (
            <button className="btn btn-primary" onClick={() => setCriando(true)}>
              <i className="ti ti-plus" /> Novo evento
            </button>
          )
        }
      />

      <div className="barra-filtros">
        <CampoBusca
          valor={busca}
          aoMudar={(v) => {
            setBusca(v);
            setPagina(1);
          }}
          placeholder="Buscar por nome, cidade ou tipo…"
        />
        <select
          className="form-select"
          style={{ width: 'auto' }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPagina(1);
          }}
        >
          <option value="">Todos os status</option>
          {Object.entries(ROTULOS_STATUS).map(([valor, rotulo]) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-calendar-star"
            titulo={busca || status ? 'Nenhum evento encontrado' : 'Nenhum evento ainda'}
            descricao={
              busca || status
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Crie o primeiro evento para começar a cadastrar participantes.'
            }
            acao={
              pode('events.create') &&
              !busca &&
              !status && (
                <button className="btn btn-primary" onClick={() => setCriando(true)}>
                  <i className="ti ti-plus" /> Novo evento
                </button>
              )
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th>Quando</th>
                    <th>Onde</th>
                    <th>Participantes</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((evento) => (
                    <tr key={evento.id}>
                      <td>
                        <Link to={`/eventos/${evento.id}`} className="celula-principal">
                          {evento.name}
                        </Link>
                        {evento.type && <div className="celula-secundaria">{evento.type}</div>}
                      </td>
                      <td>{formatarPeriodo(evento.startDate, evento.endDate)}</td>
                      <td>
                        {[evento.city, evento.state].filter(Boolean).join(' / ') || (
                          <span style={{ color: 'var(--text-dim)' }}>—</span>
                        )}
                        <div className="celula-secundaria">{rotuloFormato(evento.format)}</div>
                      </td>
                      <td>{evento._count?.participants ?? 0}</td>
                      <td>
                        <Tag type={CORES_STATUS[evento.status] ?? 'muted'}>
                          {ROTULOS_STATUS[evento.status] ?? evento.status}
                        </Tag>
                      </td>
                      <td>
                        {/* O nome já leva para cá, mas um nome que vira link não
                            se anuncia sozinho — o botão diz que existe uma tela
                            do evento por trás da linha. */}
                        <div className="acoes-linha">
                          <Link className="btn btn-secondary btn-sm" to={`/eventos/${evento.id}`}>
                            <i className="ti ti-eye" /> Abrir
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}

      {criando && (
        <Modal
          titulo="Novo evento"
          descricao="Só o nome é obrigatório agora — o resto pode ser completado depois."
          aoFechar={() => setCriando(false)}
          largura={600}
        >
          <Form aoEnviar={(dados) => criar.mutate(dados)}>
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <Campo label="Nome do evento" name="name" obrigatorio autoFocus />
            <Campo
              label="Descrição curta"
              name="shortDescription"
              ajuda="Aparece nas listagens e no contexto que a IA usa na conversa."
            />

            <LinhaCampos>
              <Campo label="Tipo" name="type" placeholder="Congresso, feira, summit…" />
              <Campo
                label="Formato"
                name="format"
                opcoes={[
                  { valor: 'PRESENCIAL', rotulo: 'Presencial' },
                  { valor: 'ONLINE', rotulo: 'Online' },
                  { valor: 'HIBRIDO', rotulo: 'Híbrido' },
                ]}
              />
            </LinhaCampos>

            <LinhaCampos>
              <Campo label="Início" name="startDate" tipo="date" />
              <Campo label="Término" name="endDate" tipo="date" />
            </LinhaCampos>

            {/* País não é decoração: é ele que permite normalizar telefone
                digitado sem DDI. Sem preencher, todo participante cadastrado
                como "11987654321" é recusado até vir com +55. */}
            <CamposLocalidade />
            <div className="campo-ajuda" style={{ marginTop: -10, marginBottom: 16 }}>
              O país define como telefones sem DDI são lidos no cadastro e na importação.
            </div>
            <input type="hidden" name="primaryLocale" value="pt-BR" />

            <ModalRodape>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCriando(false)}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Criando…' : 'Criar evento'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </>
  );
}

/** Campo vazio vira ausente: o backend valida formato, e "" não é uma data. */
export function limpar(dados: Record<string, string>) {
  return Object.fromEntries(Object.entries(dados).filter(([, v]) => v !== ''));
}

export function formatarPeriodo(inicio: string | null, fim: string | null) {
  if (!inicio) return <span style={{ color: 'var(--text-dim)' }}>Sem data</span>;

  const d = (s: string) => new Date(s).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  return fim && fim !== inicio ? `${d(inicio)} – ${d(fim)}` : d(inicio);
}

/**
 * "São Paulo, São Paulo, Brasil" — por extenso, e não "São Paulo / SP / BR".
 *
 * O banco guarda sigla e código (é o que o formulário grava); o nome vem das
 * mesmas listas do formulário. Fora do Brasil o estado é texto livre, e vai
 * como foi digitado.
 */
export function cidadeDoEvento(evento: { city: string | null; state: string | null; country: string | null }) {
  const estado =
    evento.country === 'BR' ? (UFS.find((u) => u.sigla === evento.state)?.nome ?? evento.state) : evento.state;
  const pais = PAISES.find((p) => p.codigo === evento.country)?.nome ?? evento.country;

  return [evento.city, estado, pais].filter(Boolean).join(', ');
}

export function rotuloFormato(formato: string) {
  return { PRESENCIAL: 'Presencial', ONLINE: 'Online', HIBRIDO: 'Híbrido' }[formato] ?? formato;
}
